import * as React from 'react'
import { Button, IconButton, Input } from 'uxp/components';
import { generateUUID } from '../../../util';
import { WidgetDesignerPillInput } from "../pill-input/PillInput";
import { IWDDesignModeProps } from "../common";
import { IContextData } from '../../../hooks/useContextData';
import { $L } from '../../../localize';
import './ActionEditor.scss';

interface IParamRow { id: string, key: string, value: any }

export const ActionParameterEditor: React.FunctionComponent<{ value: any, onChange: (v: any) => void, contextData?: IContextData, designer: IWDDesignModeProps, uiProps: any }> = (props) => {

    // Rows are the local source of truth (allows empty keys while typing and
    // in-place key renames). The serialized shape stays the same flat object;
    // rows without a key are never committed.
    const [rows, setRows] = React.useState<IParamRow[]>(() => hydrate(props.value))
    const lastEmitted = React.useRef<any>(props.value)

    function hydrate(params: any): IParamRow[] {
        return Object.entries(params || {}).map(([key, value]) => ({ id: generateUUID(), key, value }))
    }

    // External reseed (e.g. Execute Action picking an action re-seeds params):
    // rehydrate only when the incoming object is not the one we last emitted.
    React.useEffect(() => {
        if (props.value !== lastEmitted.current) {
            lastEmitted.current = props.value
            setRows(hydrate(props.value))
        }
    }, [props.value])

    function commit(next: IParamRow[]) {
        setRows(next)
        const obj: { [key: string]: any } = {}
        for (const r of next) {
            if (r.key.trim().length > 0) obj[r.key] = r.value
        }
        lastEmitted.current = obj
        props.onChange(obj)
    }

    function updateRow(id: string, patch: Partial<IParamRow>) {
        commit(rows.map(r => r.id === id ? { ...r, ...patch } : r))
    }

    return <div className='axn-params'>
        {rows.length > 0 &&
            <div className='axn-params__labels'>
                <span>{$L('widgetdesigner.actions.parameter-name', 'Parameter Name')}</span>
                <span>{$L('widgetdesigner.actions.parameter-value', 'Parameter Value')}</span>
            </div>
        }
        {rows.map((r) => (
            <div className='axn-params__row' key={r.id}>
                <div className='axn-params__key'>
                    <Input
                        value={r.key}
                        onChange={(v) => updateRow(r.id, { key: v })}
                        placeholder={$L('widgetdesigner.actions.key-placeholder', 'Key')}
                    />
                </div>
                <div className='axn-params__value'>
                    <WidgetDesignerPillInput
                        value={r.value}
                        onChange={(v) => updateRow(r.id, { value: v })}
                        className="widget-designer-input"
                        contextData={props.contextData}
                        showFields={true}
                    />
                </div>
                <IconButton
                    icon="fal trash"
                    variant="danger"
                    size="medium"
                    tooltip={$L('widgetdesigner.common.remove', 'Remove')}
                    onClick={() => commit(rows.filter(_r => _r.id !== r.id))}
                />
            </div>
        ))}
        <Button
            title={$L('widgetdesigner.actions.add-parameter', 'Add Parameter')}
            variant="link"
            size="small"
            leftIcon="fal plus"
            onClick={() => setRows([...rows, { id: generateUUID(), key: '', value: '' }])}
        />
    </div>;
}
